/** @jsxImportSource @emotion/react */
import css from '@emotion/css/macro'
import colors from '@sportninja/common/constants/appColors'
import { PRIMARY_BUTTON_CONTENT } from '@sportninja/common/constants/buttonColors'
import { useSports } from '@sportninja/common/hooks/useSports'
import { resolveAssetUrl } from '@sportninja/common/utils/assetUrl'
import { useContext, useEffect, useMemo, useRef } from 'react'
import Helmet from 'src/components/Helmet'
import {
  heading3,
  heading5,
  heading6,
  paragraphMedium,
  paragraphSmall,
  paragraphXSmall,
  weightBold,
  weightRegular,
  weightSemiBold,
} from 'src/components/css'
import { RegistrationContext } from '../context/RegistrationContext'
import ConfirmYourEmail from './New_ConfirmYourEmail'
import Login from './New_Login'
import RegisterReview from './New_RegisterReview'
import RegistrationPayment from './New_RegistrationPayment'
import RegistrationStart from './RegistrationStart'
import { SignUp } from './New_SignUp'
import hockeyDefaultBg from './hockey_default_bg.png'
import { Alert } from 'antd'
import SelectYourRole from './New_SelectYourRole'
import Text from 'src/components/ts-components/Text'
import useWindowSize from 'src/hooks/useWindowSize'
import Waiver from './New_Waiver'
import New_CustomRegister from './New_CustomRegister' // New team registration component, custom forms
import TeamRegister from './New_TeamRegister' // Legacy team registration component, hardcoded forms
import { useHistory, useParams } from 'react-router-dom'
import { stepForSlug, registrationStepPath } from '../registrationSteps'
import { useRegistrationStepTracking } from '../hooks/useRegistrationStepTracking'
import { isExistingSelectedPlayer } from '../utils/existingPlayerForm'

export const alternateButtonStyle = css`
  ${heading6}
  ${weightSemiBold}
  display: flex;
  flex-direction: row;
  justify-content: center;
  align-items: center;
  padding: 8px 0;
  flex: 1;
  height: 40px;
  border: 2px solid ${colors.PRIMARY};
  color: ${colors.WHITE};
  border-radius: 8px;
  cursor: pointer;
  text-transform: uppercase;
  font-family: 'BarlowCondensed', sans-serif;
  min-width: 240px;
  max-width: 240px;
  width: 240px;
  @media (max-width: 600px) {
    width: 100%;
    min-width: 0px;
    max-width: 100%;
  }
`

export const buttonStyle = css`
  ${heading6}
  ${weightSemiBold}
  background-color: ${colors.PRIMARY};
  display: flex;
  flex-direction: row;
  justify-content: center;
  align-items: center;
  padding: 8px 0;
  flex: 1;
  height: 40px;
  border: 2px solid ${colors.SOFT_STEEL};
  color: ${PRIMARY_BUTTON_CONTENT.text};
  border-radius: 8px;
  cursor: ${'pointer'};
  text-transform: uppercase;
  font-family: 'BarlowCondensed', sans-serif;
  min-width: 240px;
  max-width: 240px;
  @media (max-width: 600px) {
    width: 100%;
    min-width: 0px;
    max-width: 100%;
  }
`

export const disabledButtonStyle = css`
  ${buttonStyle}
  background-color: ${colors.SOFT_STEEL};
  cursor: not-allowed;
`

export const loadingButtonStyle = css`
  ${buttonStyle}
  background-color: ${colors.SOFT_STEEL};
  cursor: wait;
`

export const headingStyle = css`
  ${heading5}
  ${weightSemiBold}
`

export const headingThreeStyle = css`
  ${heading3}
  ${weightSemiBold}
`

export const headingSixStyle = css`
  ${heading6}
  ${weightSemiBold}
`

export const headingButtonStyle = css`
  ${heading5}
  ${weightSemiBold}
`

export const paragraphSmallStyle = css`
  ${paragraphSmall}
  ${weightRegular}
`

export const paragraphMediumStyle = css`
  ${paragraphMedium}
  ${weightRegular}
`

export const paragraphSmallBoldStyle = css`
  ${paragraphSmall}
  ${weightBold}
`

export const paragraphMediumBoldStyle = css`
  ${paragraphMedium}
  ${weightBold}
`

export const paragraphXSmallStyle = css`
  ${paragraphXSmall}
  ${weightRegular}
`

export const PageTitle = ({
  title,
  subtitle,
  showAlert = false,
  showSuccess = false,
  // The rule normally closes the heading off from the content below it. A
  // caller that puts its own content between the two — a field the heading
  // introduces, say — turns it off and draws the rule itself, underneath.
  showDivider = true,
}) => {
  return (
    <div
      css={css`
        display: flex;
        flex-direction: column;
        align-items: flex-start;
        gap: 8px;
        align-self: stretch;
        margin-top: 24px;
        margin-bottom: 24px;
      `}
    >
      {showAlert ? (
        <Alert
          description='Do Not Close This Page Until You Have Completed The Registration Process.'
          type='warning'
          showIcon
          css={css`
            width: 100%;
            padding: 16px 16px;
          `}
        />
      ) : null}
      {showSuccess ? (
        <Alert
          description='Your Registration Has Been Successfully Submitted.'
          type='success'
          showIcon
          css={css`
            width: 100%;
            padding: 16px 16px;
          `}
        />
      ) : null}
      <Text variant='heading3' weight='semiBold' color={colors.NEUTRAL_0}>
        {title}
      </Text>
      {subtitle ? (
        <Text
          variant='paragraphMedium'
          weight='regular'
          color={colors.NEUTRAL_50}
        >
          {subtitle}
        </Text>
      ) : null}
      {showDivider ? (
        <div
          css={css`
            width: 100%;
            height: 1px;
            background-color: ${colors.VERY_DARK_GRAYISH_BLUE};
          `}
        />
      ) : null}
    </div>
  )
}

export const REGISTRATION_STEPS = {
  SELECT_YOUR_DIVISION: 0,
  SIGN_UP: 1,
  LOGIN: 2,
  TEAM_REGISTER: 3,
  REGISTRATION_PAYMENT: 4,
  CONFIRM_YOUR_EMAIL: 5,
  REGISTER_REVIEW: 6,
  SELECT_YOUR_ROLE: 7,
  WAIVER: 8,
}

const getBackgroundImage = (
  genericSportForGame,
  isSoccer,
  imageName,
  isDesktop,
  sport = null
) => {
  if (!genericSportForGame) {
    if (isSoccer) {
      return '/images/soccer_game_background'
    } else {
      if (sport && sport?.name) {
        if (sport?.name === 'lacrosse') {
          return '/images/grass'
        }
        if (sport?.name === 'fieldhockey') {
          return '/images/grass'
        }
        if (sport?.name === 'floorball') {
          return '/images/floorball'
        }
      }
      return '/images/game_hockey'
    }
  } else {
    return isDesktop
      ? genericSportForGame?.background_web_image?.replace('.png', '')
      : genericSportForGame?.background_mobile_image?.replace('.png', '')
  }
}

const Main = () => {
  const {
    registration,
    genericSportProperties,
    role,
    isTeamRep,
    selectedPlayer,
    selectedTeam,
    division,
  } = useContext(RegistrationContext)

  // The URL slug is the single source of truth for the current step. index.jsx
  // reconciles the slug to the server's step on load, so by the time Main
  // renders, the slug is normally valid.
  const { id, step: stepSlug } = useParams()
  const history = useHistory()
  const step = stepForSlug(stepSlug)

  // Analytics: one `registration_step` event per step the user lands on. IDs
  // and enums only — no name/email/birth date (PII is not permitted in GA4).
  //
  // player_id is sent only when registering a player. The team-rep path reuses
  // `selectedPlayer` to hold the chosen *team official*, so sending it there
  // would mix two different kinds of id into one dimension. It is also omitted
  // for a not-yet-created player, whose `'new'` sentinel is not a real id and
  // would collide across unrelated users.
  const isRegisteringExistingPlayer =
    !isTeamRep && isExistingSelectedPlayer(selectedPlayer)

  useRegistrationStepTracking({
    stepSlug,
    data: {
      registration_id: id,
      registration_type: role?.name,
      is_team_rep: isTeamRep,
      player_id: isRegisteringExistingPlayer ? selectedPlayer?.id : undefined,
      // Teams are keyed by `uid` with `id` as a fallback, matching how the rest
      // of the flow reads them (New_CustomRegister, TeamInfoSection).
      team_id: selectedTeam?.uid || selectedTeam?.id,
      division_id: division?.schedule_id,
    },
  })

  // Self-heal a bad/missing/retired slug: Main only renders once a session
  // exists, but a hand-edited or stale URL (unknown slug, or the bare
  // /registration/:id) would resolve to a null step and render a blank step area
  // with no in-app path to recover (the load reconcile in index.jsx is gated on
  // !session). Redirect to the first step so the user is never stranded.
  //
  // `select-role` is included because it is retired, not unknown: role and
  // division are one question now, asked on the first step. A bookmark or a
  // browser-history entry from before the merge would otherwise still render the
  // old two-screen version, which no longer matches the session it reads.
  useEffect(() => {
    if ((step === null || step === REGISTRATION_STEPS.SELECT_YOUR_ROLE) && id) {
      history.replace(
        registrationStepPath(
          id,
          REGISTRATION_STEPS.SELECT_YOUR_DIVISION,
          history.location.search
        )
      )
    }
  }, [step, id, history])

  const { width } = useWindowSize()
  const isDesktop = width > 768
  const { sports, fetchSports } = useSports()

  /**
   * Returns component map for the Registration wizard based on registration version.
   *
   * @description Determines which registration component to render at step 3 based on
   * the registration_version field from the API response.
   *
   * @param {number} registration_version - Version identifier from root_schedule
   *   - version 1: Uses legacy TeamRegister screen with existing functionality
   *   - any other value (including undefined): Uses new dynamic CustomRegister screen
   *
   * @returns {Object} Component mapping for all registration steps
   *
   * @memoized Based on registration_version to prevent unnecessary re-renders
   * and ensure consistent component selection throughout the registration flow.
   */
  const stepsMap = useMemo(() => {
    const isLegacy =
      registration?.registration?.root_schedule?.registration_version === 1
    return {
      // Merged division + role step. New_SelectYourDivision / New_SelectYourRole
      // are kept on disk as reference until this has shipped.
      0: RegistrationStart,
      1: SignUp,
      2: Login,
      3: isLegacy ? TeamRegister : New_CustomRegister, // ★ version switch
      4: RegistrationPayment,
      5: ConfirmYourEmail,
      6: RegisterReview,
      7: SelectYourRole,
      8: Waiver,
    }
  }, [registration?.registration?.root_schedule?.registration_version])

  useEffect(() => {
    if (!sports || sports?.length === 0) {
      fetchSports()
    }
  }, [fetchSports, sports])

  const sport = sports?.find((s) => s?.id === registration?.sport_id)
  const isSoccer = sport?.name === 'soccer'
  const isGenericSport =
    registration?.registration?.root_schedule?.generic_sport_id !== null
  const genericSport = isGenericSport
    ? genericSportProperties?.find(
        (g) =>
          g?.id === registration?.registration?.root_schedule?.generic_sport_id
      )
    : null

  const backgroundImage = getBackgroundImage(
    genericSport,
    isSoccer,
    'game_hockey',
    isDesktop,
    sport
  )

  const Step = useMemo(
    () => (step === null ? null : stepsMap[step]),
    [stepsMap, step]
  )
  const divRef = useRef(null)

  const apiBackgroundImage = useMemo(() => {
    return (
      resolveAssetUrl(registration?.registration?.image) ||
      `${backgroundImage}.png`
    )
  }, [registration?.registration?.image?.full_path, backgroundImage])

  useEffect(() => {
    document.body.classList.add('keyboard')
    document.body.addEventListener(
      'focus',
      (event) => {
        const target = event.target
        switch (target.tagName) {
          case 'INPUT':
          case 'TEXTAREA':
          case 'SELECT':
            document.body.classList.add('keyboard')
        }
      },
      true
    )
    document.body.addEventListener(
      'blur',
      () => {
        document.body.classList.remove('keyboard')
      },
      true
    )
    return () => {
      document.body.removeEventListener('focus', () => {
        document.body.classList.remove('keyboard')
      })
      document.body.removeEventListener('blur', () => {
        document.body.classList.remove('keyboard')
      })
    }
  }, [])

  return (
    <div
      css={css`
        display: flex;
        width: 100%;
        justify-content: center;
        align-items: center;
        flex-direction: column;
        /* Adjustment for the Background issue */
        /* max-height: 100vh; */
        background-color: ${colors.NEUTRAL_800};
        z-index: -1;
      `}
    >
      <style>
        {`
            body.keyboard {
              height: calc(100% + 500px); /* add padding for keyboard */
            }
            input {
              font-size: 16px !important;
            }
          `}
      </style>
      <img
        src={'/images/powered_by_sportninja.png'}
        css={css`
          height: 30px;
          max-width: 180px;
          position: absolute;
          bottom: ${step === REGISTRATION_STEPS.TEAM_REGISTER
            ? '-700px'
            : '-150px'};
          left: 16px;
          z-index: 3;
          display: none;
          /* hide on mobile */
          @media (max-width: 768px) {
            display: none;
          }
        `}
      />
      <Helmet title='Registration' />
      <div
        ref={divRef}
        css={css`
          background-image: url(${apiBackgroundImage});
          background-position: center;
          background-size: cover;
          background-repeat: no-repeat;
          position: fixed;
          top: 0;
          left: 0;
          width: 100vw;
          /* Adjustment for the Background issue */
          height: 40vh;
          z-index: 1;
        `}
      >
        <img
          src={apiBackgroundImage}
          onError={() => {
            if (divRef.current) {
              divRef.current.style.backgroundImage = `url(${hockeyDefaultBg})`
            }
          }}
          css={css`
            display: none;
          `}
        />
      </div>
      <div
        css={css`
          /* overflow-y: auto; */
          margin-top: 100px;
          /* margin-top: 160px; */
          background-color: ${colors.NEUTRAL_800};
          border-radius: 16px 16px 0px 0px;
          display: flex;
          flex-direction: column;
          display: flex;
          min-width: 1080px;
          flex-direction: column;
          align-items: center;
          gap: 24px;
          flex-shrink: 0;
          /* height: 100%; */
          position: absolute;
          bottom: 0;
          top: 0;
          /* if the step is TeamRegister, we should make this 150% */
          height: ${'100%'};
          @media (max-width: 768px) {
            width: 100%;
            min-width: 0;
          }
          > * {
            color: #000;
            background-color: ${colors.NEUTRAL_800};
          }
          /* Adjustment for the Background issue */
          z-index: 2;
        `}
      >
        <div
          css={css`
            width: 100%;
            height: 100%;
            border-top-right-radius: 16px;
            border-top-left-radius: 16px;
          `}
        >
          {Step ? <Step isDarkMode={false} /> : null}
        </div>
      </div>
    </div>
  )
}

export default Main
